import React from "react"
import { Menu as MenuPrimitive } from "@base-ui/react/menu"
import type { MenuRootChangeEventDetails } from "@base-ui/react/menu"
import { MenuTrigger } from "./menu-trigger"
import { MenuContent } from "./menu-content"
import { MenuItem } from "./menu-item"
import { MenuItemIcon } from "./menu-item-icon"
import { MenuItemLabel } from "./menu-item-label"
import { MenuItemDescription } from "./menu-item-description"
import { MenuItemAvatar } from "./menu-item-avatar"
import { MenuItemTrailing } from "./menu-item-trailing"
import { MenuGroup } from "./menu-group"
import { MenuHeader } from "./menu-header"
import { MenuDivider } from "./menu-divider"
import { MenuSubmenu } from "./menu-submenu"
import { MenuSubmenuTrigger } from "./menu-submenu-trigger"
import { MenuSubmenuContent } from "./menu-submenu-content"

export type { MenuTriggerProps } from "./menu-trigger"
export type { MenuContentProps } from "./menu-content"
export type { MenuItemProps } from "./menu-item"
export type { MenuItemAvatarProps } from "./menu-item-avatar"
export type { MenuGroupProps } from "./menu-group"
export type { MenuHeaderProps } from "./menu-header"
export type { MenuDividerProps } from "./menu-divider"
export type { MenuSubmenuProps } from "./menu-submenu"
export type { MenuSubmenuTriggerProps } from "./menu-submenu-trigger"

// =============================================================================
// Props
// =============================================================================

export type MenuProps = {
  /** The trigger and content for the menu */
  children?: React.ReactNode
  /** Whether the menu is open (controlled) */
  open?: boolean
  /** Whether the menu starts open (uncontrolled) */
  defaultOpen?: boolean
  /** Called when the open state changes */
  onOpenChange?: (open: boolean, eventDetails: MenuRootChangeEventDetails) => void
}

// =============================================================================
// Root
// =============================================================================

/**
 * A dropdown menu that opens on click, providing a list of actions or options.
 * Built on BaseUI's Menu primitive for full keyboard navigation and ARIA support.
 *
 * Compose with `Menu.Trigger`, `Menu.Content`, `Menu.Item`, `Menu.Header`,
 * `Menu.Divider`, `Menu.Submenu`, `Menu.SubmenuTrigger`, and `Menu.SubmenuContent` to build the full menu.
 *
 * @example
 * ```tsx
 * <Menu>
 *   <Menu.Trigger>Options</Menu.Trigger>
 *   <Menu.Content>
 *     <Menu.Group>
 *       <Menu.Header>Actions</Menu.Header>
 *       <Menu.Item onClick={() => console.log("edit")}>
 *         <Menu.ItemLabel>Edit</Menu.ItemLabel>
 *       </Menu.Item>
 *     </Menu.Group>
 *     <Menu.Divider />
 *     <Menu.Item destructive onClick={() => console.log("delete")}>
 *       <Menu.ItemLabel>Delete</Menu.ItemLabel>
 *     </Menu.Item>
 *   </Menu.Content>
 * </Menu>
 * ```
 */
function MenuRoot({ children, open, defaultOpen, onOpenChange }: MenuProps) {
  return (
    <MenuPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {children}
    </MenuPrimitive.Root>
  )
}

MenuRoot.displayName = "Menu"

// =============================================================================
// Compound component
// =============================================================================

interface MenuComponent {
  (props: MenuProps): React.JSX.Element
  Trigger: typeof MenuTrigger
  Content: typeof MenuContent
  Item: typeof MenuItem
  ItemIcon: typeof MenuItemIcon
  ItemLabel: typeof MenuItemLabel
  ItemAvatar: typeof MenuItemAvatar
  ItemDescription: typeof MenuItemDescription
  ItemTrailing: typeof MenuItemTrailing
  Group: typeof MenuGroup
  Header: typeof MenuHeader
  Divider: typeof MenuDivider
  Submenu: typeof MenuSubmenu
  SubmenuTrigger: typeof MenuSubmenuTrigger
  SubmenuContent: typeof MenuSubmenuContent
}

export const Menu: MenuComponent = Object.assign(MenuRoot, {
  Trigger: MenuTrigger,
  Content: MenuContent,
  Item: MenuItem,
  ItemIcon: MenuItemIcon,
  ItemLabel: MenuItemLabel,
  ItemAvatar: MenuItemAvatar,
  ItemDescription: MenuItemDescription,
  ItemTrailing: MenuItemTrailing,
  Group: MenuGroup,
  Header: MenuHeader,
  Divider: MenuDivider,
  Submenu: MenuSubmenu,
  SubmenuTrigger: MenuSubmenuTrigger,
  SubmenuContent: MenuSubmenuContent,
} as const)
